import { NoticeBanner, NoticeModal, type Notice } from '@hkdevs/design-system';
import { useState } from 'react';
import { useAcknowledgeNotice, useMyNotices, type UserNotice } from '../api/queries/notices';
import { linkify } from '../views/notices/NoticeEditor';

/**
 * The signed-in user's notices (pc-dbb.19): the active ones they haven't acknowledged, in
 * the shell's banner slot, and the newest blocking one in a dialog that only "I
 * acknowledge" closes. A notice that needs no acknowledgement can be dismissed for the
 * session. A failed acknowledgement keeps the notice, with the error under it.
 *
 * While an admin views as someone, nothing shows: those notices are the other user's,
 * and the API refuses to acknowledge them on their behalf (ADR 0017).
 */

/** Where this session's dismissed notices are kept. */
export const DISMISSED_KEY = 'pc.dismissedNotices';

function readDismissed(): ReadonlySet<string> {
  try {
    return new Set(JSON.parse(sessionStorage.getItem(DISMISSED_KEY) ?? '[]') as string[]);
  } catch {
    return new Set();
  }
}

function writeDismissed(ids: ReadonlySet<string>) {
  try {
    sessionStorage.setItem(DISMISSED_KEY, JSON.stringify([...ids]));
  } catch {
    // Private mode or blocked storage: the dismissal lasts until the page reloads.
  }
}

const ACK_FAILED = 'Couldn’t save your acknowledgement. Try again.';

const asNotice = (notice: UserNotice): Notice => ({
  id: notice.id,
  tone: notice.tone,
  title: notice.title,
  ...(notice.body && { body: linkify(notice.body) }),
  requiresAck: notice.requiresAck,
});

/** The banner (undefined when nothing shows) and the blocking dialog. */
export function useUserNotices(enabled: boolean) {
  const notices = useMyNotices(enabled);
  const acknowledge = useAcknowledgeNotice();
  const [dismissed, setDismissed] = useState(readDismissed);
  const [failed, setFailed] = useState<string | null>(null);
  const list = enabled ? (notices.data ?? []) : [];

  const blocking = list.find((notice) => notice.blocking && notice.requiresAck) ?? null;
  const shown = list.filter(
    (notice) => notice !== blocking && (notice.requiresAck || !dismissed.has(notice.id)),
  );
  const acknowledging = acknowledge.isPending ? (acknowledge.variables ?? null) : null;

  const ack = (id: string) => {
    setFailed(null);
    acknowledge.mutate(id, { onError: () => setFailed(id) });
  };
  const dismiss = (id: string) => {
    const next = new Set(dismissed).add(id);
    setDismissed(next);
    writeDismissed(next);
  };

  const banner =
    shown.length > 0 ? (
      <NoticeBanner
        notices={shown.map(asNotice)}
        acknowledging={acknowledging}
        acknowledgeError={failed}
        onAcknowledge={ack}
        onDismiss={dismiss}
      />
    ) : undefined;
  const modal = (
    <NoticeModal
      notice={blocking && asNotice(blocking)}
      busy={blocking !== null && acknowledging === blocking.id}
      error={blocking !== null && failed === blocking.id ? ACK_FAILED : undefined}
      onAcknowledge={ack}
    />
  );
  return { banner, modal };
}
